/**
 * Event mutation hooks using generic factory
 */

import { eventApi } from '@/lib/api/events.api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateEvent,
  Event,
  UpdateEvent,
  UpdateEventModuleLinksInput,
} from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'

const mutations = createResourceMutations<Event, CreateEvent, UpdateEvent>({
  resourceName: 'Event',
  resourceNameSingular: 'event',
  queryKeyFactory: queryKeys.events,
  api: {
    create: (data: CreateEvent) => eventApi.createEvent(data),
    update: (id: string, data: UpdateEvent) => eventApi.updateEvent(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateEvent
      return eventApi.updateEvent(id, updateData)
    },
    delete: (id: string) => eventApi.deleteEvent(id),
  },
  enableRealtime: false,
})

export const useCreateEventMutation = mutations.useCreateMutation
export const useUpdateEventMutation = mutations.useUpdateMutation
export const useUpdateEventFieldMutation = mutations.useUpdateFieldMutation!
export const useDeleteEventMutation = mutations.useDeleteMutation

/**
 * Bulk delete events mutation hook
 */
export function useBulkDeleteEventsMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ids: string[]) => eventApi.bulkDeleteEvents(ids),
    onSuccess: result => {
      toast.success(
        `${result.deletedCount} event${result.deletedCount === 1 ? '' : 's'} deleted`,
      )
      // Invalidate events list
      queryClient.invalidateQueries({ queryKey: queryKeys.events.lists() })
    },
    onError: error => {
      toast.error('Failed to delete events')
      console.error('Bulk delete events error:', error)
    },
  })
}

/**
 * Set the event's owner (its primary link). Organization is passed as an
 * explicit link to the current org; the server keeps the link table and the
 * event's moduleType/moduleId columns in sync.
 */
export function useUpdateEventOwnerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      owner,
    }: {
      id: string
      owner: NonNullable<UpdateEventModuleLinksInput['primaryModuleLink']>
    }) =>
      eventApi.updateEventModuleLinks(id, {
        primaryModuleLink: owner,
        moduleLinks: [],
      }),
    onSuccess: (_event, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.events.detail(id) })
    },
    onError: () => {
      toast.error('Failed to update event owner')
    },
  })
}
